import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useTranslate } from 'src/locales';
import { useGetJobCandidatesQuery } from 'src/services/applications/applications.service';
import { MAX_BULK_CANDIDATES, PresentationExportFormat } from 'src/services/presentations/presentations.types';
import { useGetPresentationTemplatesQuery, useCreatePresentationPackMutation } from 'src/services/presentations/presentations.service';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  /** The template to start on, when the caller has a better one than the agency's default (a deal's marketing profiles use the anonymous one). */
  defaultTemplateKey?: string;
  /** Starts with anonymising on or off, whatever the template says. */
  defaultAnonymise?: boolean;
  defaultTitle?: string;
} & (
  /** From the candidate list: OrganizationCandidate ids. */
  | { candidateIds: string[]; jobId?: undefined; applicationIds?: undefined }
  /** From the job pipeline: the ticked applications on that job. */
  | { jobId: string; applicationIds: string[]; candidateIds?: undefined }
);

/**
 * "Create presentations" for several candidates at once (JT1-1887, C4): one file each, made in the
 * background and zipped. Each candidate's own presentation is used (for the job, when started from a
 * job); one is made from the template for anyone who doesn't have one. The recruiter is told when the
 * zip is ready.
 */
export default function BulkPresentationsDialog({ open, onClose, candidateIds, jobId, applicationIds, defaultTemplateKey, defaultAnonymise, defaultTitle }: Props) {
  const { t } = useTranslate();
  const [templateKey, setTemplateKey] = useState('');
  const [format, setFormat] = useState<PresentationExportFormat>(PresentationExportFormat.PDF);
  const [anonymise, setAnonymise] = useState(false);
  const [title, setTitle] = useState(defaultTitle ?? '');

  const { currentData: templates } = useGetPresentationTemplatesQuery(undefined, { skip: !open });
  // The same arguments the pipeline's board loads with, so this is usually served from its cache.
  const { currentData: applications } = useGetJobCandidatesQuery({ jobId: jobId as string, params: { page: 1, per_page: 999 } }, { skip: !open || !jobId });
  const [createPack, { isLoading }] = useCreatePresentationPackMutation();

  const items = useMemo(() => {
    if (candidateIds) return candidateIds.map((id) => ({ organization_candidate_id: id }));
    // Only applicants the agency has a record for can be presented.
    const byId = new Map((applications?.results ?? []).map((application) => [application.id, application]));
    return (applicationIds ?? []).flatMap((id) => {
      const candidate = byId.get(id)?.org_candidate?.id;
      return candidate ? [{ organization_candidate_id: candidate, application_id: id }] : [];
    });
  }, [candidateIds, applicationIds, applications]);

  const chosen = candidateIds?.length ?? applicationIds?.length ?? 0;
  const loadingApplicants = !!jobId && !applications;
  const skipped = loadingApplicants ? 0 : chosen - items.length;
  const tooMany = items.length > MAX_BULK_CANDIDATES;

  useEffect(() => {
    const preferred = templates?.find((template) => template.key === defaultTemplateKey) ?? templates?.find((template) => template.is_default) ?? templates?.[0];
    if (preferred && !templateKey) {
      setTemplateKey(preferred.key);
      setAnonymise(defaultAnonymise ?? !!preferred.anonymise_by_default);
    }
  }, [templates, templateKey, defaultTemplateKey, defaultAnonymise]);

  const handleCreate = async () => {
    try {
      await createPack({
        kind: 'bulk',
        job_id: jobId,
        items,
        template_key: templateKey || undefined,
        format,
        title: title.trim() || undefined,
        anonymise,
      }).unwrap();
      enqueueSnackbar(t('presentations.bulk.started'));
      onClose();
    } catch {
      enqueueSnackbar(t('presentations.bulk.failed'), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('presentations.bulk.title')}
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {t('presentations.bulk.subtitle', { count: items.length })}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {skipped > 0 && <Alert severity="info">{t('presentations.bulk.skipped', { count: skipped })}</Alert>}
          {tooMany && <Alert severity="warning">{t('presentations.bulk.too_many', { count: MAX_BULK_CANDIDATES })}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              fullWidth
              label={t('presentations.packs.template')}
              value={templates?.some((template) => template.key === templateKey) ? templateKey : ''}
              onChange={(event) => setTemplateKey(event.target.value)}
              helperText={t('presentations.bulk.template_helper')}
            >
              {(templates ?? []).map((template) => (
                <MenuItem key={template.key} value={template.key}>{template.name}</MenuItem>
              ))}
            </TextField>

            <TextField select fullWidth label={t('presentations.export.format')} value={format} onChange={(event) => setFormat(event.target.value as PresentationExportFormat)}>
              <MenuItem value={PresentationExportFormat.PDF}>{t('presentations.packs.pdf')}</MenuItem>
              <MenuItem value={PresentationExportFormat.DOCX}>{t('presentations.packs.docx')}</MenuItem>
              <MenuItem value={PresentationExportFormat.PPTX}>{t('presentations.packs.pptx')}</MenuItem>
            </TextField>
          </Stack>

          <TextField
            fullWidth
            label={t('presentations.bulk.title_label')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            inputProps={{ maxLength: 200 }}
          />

          <FormControlLabel
            control={<Switch checked={anonymise} onChange={(event) => setAnonymise(event.target.checked)} />}
            label={(
              <Box>
                <Typography variant="subtitle2">{t('presentations.anonymise.toggle')}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('presentations.packs.anonymise_helper')}</Typography>
              </Box>
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={!templates || loadingApplicants || !items.length || tooMany} onClick={handleCreate}>
          {t('presentations.bulk.submit', { count: items.length })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
